// MobileExpenses.js — Kiosk-only view-only expense viewer at /kiosk/expenses.
// Inline auth (OTP or password fallback), requires remoteExpenseAccess + expenses.view.
// Card-based list, collapsible analytics. No add/edit/delete.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useKioskManifest from '../hooks/useKioskManifest';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line,
} from 'recharts';

const API = process.env.REACT_APP_URL;
const KIOSK_AUTH_KEY = 'kiosk_expenses_auth';

/* ─── helpers ──────────────────────────────────────────────────── */
const fmt   = n  => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDt = dt => dt ? new Date(dt).toLocaleString('en-IN',{ day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', hour12:true }) : '';

function normStr(s) { return (s||'').toLowerCase().trim().replace(/\s+/g,' '); }
function levenshtein(a,b) {
  if(a===b) return 0;
  const m=a.length,n=b.length;
  if(!m) return n; if(!n) return m;
  let prev=Array.from({length:n+1},(_,i)=>i), curr=Array(n+1).fill(0);
  for(let i=1;i<=m;i++){
    curr[0]=i;
    for(let j=1;j<=n;j++)
      curr[j]=a[i-1]===b[j-1]?prev[j-1]:1+Math.min(prev[j],curr[j-1],prev[j-1]);
    const tmp=prev; prev=curr; curr=tmp;
  }
  return prev[n];
}
function buildCanonicalMap(expenses) {
  const groups=[];
  expenses.forEach(e=>{
    const raw=(e.description||'').trim(); if(!raw) return;
    const norm=normStr(raw);
    const thr=Math.max(1,Math.floor(norm.length/5));
    let g=groups.find(g=>levenshtein(g.key,norm)<=thr);
    if(!g){g={key:norm,forms:{}};groups.push(g);}
    g.forms[raw]=(g.forms[raw]||0)+1;
  });
  const map={};
  groups.forEach(g=>{
    const canonical=Object.entries(g.forms).sort((a,b)=>b[1]-a[1])[0][0];
    Object.keys(g.forms).forEach(raw=>{map[normStr(raw)]=canonical;});
  });
  return map;
}

/* ─── Geo helper ──────────────────────────────────────────────── */
function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('GPS not supported'));
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      err => reject(err.code === 1
        ? new Error('Location access denied. Tap the lock icon in your address bar, set Location to "Allow", then reload.')
        : new Error('Unable to get location. Enable GPS and try again.')),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
}

function getSavedAuth() {
  try {
    const raw = localStorage.getItem(KIOSK_AUTH_KEY);
    if (!raw) return null;
    const auth = JSON.parse(raw);
    if (auth.expiresAt && Date.now() < auth.expiresAt) return auth;
    localStorage.removeItem(KIOSK_AUTH_KEY);
    return null;
  } catch (e) { return null; }
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
export default function MobileExpenses() {
  useKioskManifest('/manifest-expenses.json', '#78350f');
  // If user is already logged in via main app, use that session directly (no kiosk OTP)
  const [kioskAuth, setKioskAuth] = useState(() => {
    const mainToken = sessionStorage.getItem('token');
    if (mainToken) {
      try {
        const saved = JSON.parse(localStorage.getItem('auth') || '{}');
        return {
          token: mainToken,
          username: saved.username || sessionStorage.getItem('lu') || '',
          roles: saved.roles || [],
          permissions: saved.permissions || [],
          remoteExpenseAccess: true, // navigated via menu → access granted
          fromMainApp: true,
        };
      } catch (e) { /* fall through to kiosk auth */ }
    }
    return getSavedAuth();
  });
  const [authUser, setAuthUser]       = useState('');
  const [authOtp, setAuthOtp]         = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authStep, setAuthStep]       = useState('username'); // 'username' | 'otp' | 'password'
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError]     = useState('');
  const [authResend, setAuthResend]   = useState(0);
  const authTimerRef = useRef(null);
  const otpRef = useRef(null);
  const pwRef = useRef(null);

  // Geo state (for OTP request only — coords sent to backend)
  const [coords, setCoords] = useState(null);

  // Expenses data
  const [expenses, setExpenses] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState(''); // '' | 'credit' | 'debit'
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDesc, setFilterDesc] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // Add-to-home-screen
  const [showInstall, setShowInstall] = useState(false);
  const deferredPrompt = useRef(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  // Access checks — super roles (owner/solution) always have full access
  const isSuperRole = (kioskAuth?.roles || []).some(r => ['owner', 'solution'].includes(r));
  const hasAccess = isSuperRole || (kioskAuth?.remoteExpenseAccess && (kioskAuth?.permissions || []).includes('expenses.view'));
  const canViewAnalytics = isSuperRole || (kioskAuth?.permissions || []).includes('expenses.approve');

  // ── Dynamic favicon ──
  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    const ctx = canvas.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 64, 64);
    g.addColorStop(0, '#059669'); g.addColorStop(1, '#10b981');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(32, 32, 32, 0, Math.PI * 2); ctx.fill();
    // Rupee sign
    ctx.fillStyle = '#fff'; ctx.font = 'bold 32px Arial'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('\u20B9', 32, 28);
    ctx.font = 'bold 11px Arial';
    ctx.fillText('MYC', 32, 56);
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.rel = 'icon'; link.href = canvas.toDataURL();
    document.head.appendChild(link);
    document.title = 'MYC — Expenses';
  }, []);

  // ── Get coords silently (for OTP request) ──
  useEffect(() => {
    getGeo().then(c => setCoords(c)).catch(() => {});
  }, []);

  // ── Install prompt (Android PWA / iOS Add to Home Screen) ──
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); deferredPrompt.current = e; };
    window.addEventListener('beforeinstallprompt', handler);
    if (!localStorage.getItem('mexp_shortcut_prompted')) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS || !('beforeinstallprompt' in window)) {
        setShowInstall(true);
        localStorage.setItem('mexp_shortcut_prompted', '1');
      }
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
    }
    setShowInstall(false);
    localStorage.setItem('mexp_shortcut_prompted', '1');
  };

  // ── Kiosk auth handlers (OTP + password fallback) ──
  function startAuthResend() {
    setAuthResend(60);
    clearInterval(authTimerRef.current);
    authTimerRef.current = setInterval(() => {
      setAuthResend(t => { if (t <= 1) { clearInterval(authTimerRef.current); return 0; } return t - 1; });
    }, 1000);
  }

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!authUser.trim()) return setAuthError('Please enter your username.');
    setAuthLoading(true); setAuthError('');
    try {
      // Ensure fresh GPS coords before sending (background fetch may not be ready)
      let loc = coords;
      if (!loc) {
        try { loc = await getGeo(); setCoords(loc); } catch (e) { /* backend will handle missing coords */ }
      }
      const res = await fetch(`${API}/kiosk-otp/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: authUser.trim(), userLat: loc?.lat, userLon: loc?.lon }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP');
      if (data.mode === 'password') {
        setAuthStep('password');
      } else {
        setAuthStep('otp');
        startAuthResend();
        if (data.devOtp) setAuthError(`[DEV] OTP: ${data.devOtp}`);
      }
    } catch (err) { setAuthError(err.message); }
    finally { setAuthLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const isPassword = authStep === 'password';
    if (!isPassword && (!authOtp || authOtp.length !== 4)) return setAuthError('Enter the 4-digit OTP.');
    if (isPassword && !authPassword) return setAuthError('Enter your password.');
    setAuthLoading(true); setAuthError('');
    try {
      const body = { username: authUser.trim() };
      if (isPassword) body.password = authPassword;
      else body.otp = authOtp.trim();
      const res = await fetch(`${API}/kiosk-otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || (isPassword ? 'Invalid password' : 'Invalid OTP'));
      const d = data.data;
      const kioskDays = d.kioskSessionDays || 5;
      const authObj = {
        token: d.token, username: d.username,
        permissions: d.permissions || [], roles: d.roles || [],
        remoteExpenseAccess: !!d.remoteExpenseAccess,
        expiresAt: Date.now() + kioskDays * 24 * 60 * 60 * 1000,
      };
      localStorage.setItem(KIOSK_AUTH_KEY, JSON.stringify(authObj));
      setKioskAuth(authObj);
    } catch (err) { setAuthError(err.message); }
    finally { setAuthLoading(false); }
  };

  useEffect(() => {
    if (authStep === 'otp' && otpRef.current) otpRef.current.focus();
    if (authStep === 'password' && pwRef.current) pwRef.current.focus();
  }, [authStep]);

  // ── API helper: always kiosk token ──
  const apiGet = useCallback(async (url) => {
    if (!kioskAuth?.token) return { data: {} };
    const hdrs = { Authorization: `Bearer ${kioskAuth.token}`, 'Content-Type': 'application/json' };
    if (kioskAuth.username) hdrs['x-username'] = kioskAuth.username.toLowerCase();
    return fetch(url, { headers: hdrs })
      .then(r => r.json()).then(data => ({ data }));
  }, [kioskAuth]);

  // ── Fetch expenses ──
  const fetchRangeExpenses = useCallback(() => {
    const from = new Date(dateFrom + 'T00:00:00').toISOString();
    const to   = new Date(dateTo   + 'T23:59:59').toISOString();
    apiGet(`${API}/expenses?limit=9999&page=1&from=${from}&to=${to}`)
      .then(res => {
        const raw = res.data?.expenses || [];
        const canonMap = buildCanonicalMap(raw);
        setExpenses(raw.map(e => ({
          ...e,
          description: e.description ? (canonMap[normStr(e.description)] || e.description) : e.description,
        })));
      })
      .catch(console.error);
  }, [dateFrom, dateTo, apiGet]);

  const fetchAllExpenses = useCallback(() => {
    apiGet(`${API}/expenses?limit=9999&page=1`)
      .then(res => {
        const raw = res.data?.expenses || [];
        setAllExpenses(raw);
      })
      .catch(console.error);
  }, [apiGet]);

  useEffect(() => { if (hasAccess) { fetchRangeExpenses(); fetchAllExpenses(); } }, [hasAccess, fetchRangeExpenses, fetchAllExpenses]);

  // ── Computed values ──
  const uniqueCategories = useMemo(() => [...new Set(expenses.map(e => e.category).filter(Boolean))].sort(), [expenses]);

  const filtered = expenses.filter(e => {
    if (filterType === 'credit' && e.type !== 'credit') return false;
    if (filterType === 'debit' && e.type === 'credit') return false;
    if (filterCategory && e.category !== filterCategory) return false;
    if (filterDesc && !(e.description || '').toLowerCase().includes(filterDesc.toLowerCase())) return false;
    if (!searchQuery) return true;
    return Object.values(e).some(v => String(v || '').toLowerCase().includes(searchQuery.toLowerCase()));
  });
  const sorted = useMemo(() => [...filtered].sort((a, b) => new Date(b.date) - new Date(a.date)), [filtered]);
  const totalCredit = expenses.reduce((s, e) => s + (e.type === 'credit' ? parseFloat(e.amount) || 0 : 0), 0);
  const totalDebit  = expenses.reduce((s, e) => s + (e.type !== 'credit' ? parseFloat(e.amount) || 0 : 0), 0);
  const overallCredit = allExpenses.reduce((s, e) => s + (e.type === 'credit' ? parseFloat(e.amount) || 0 : 0), 0);
  const overallDebit  = allExpenses.reduce((s, e) => s + (e.type !== 'credit' ? parseFloat(e.amount) || 0 : 0), 0);

  // Analytics data
  const byCat = useMemo(() => {
    const m = {};
    expenses.filter(e => e.type !== 'credit').forEach(e => { const k = e.category || 'Other'; m[k] = (m[k] || 0) + (parseFloat(e.amount) || 0); });
    return Object.entries(m).map(([name, value]) => ({ name, value: Math.round(value) })).sort((a, b) => b.value - a.value);
  }, [expenses]);

  const byMonth = useMemo(() => {
    const m = {};
    allExpenses.forEach(e => {
      const d = new Date(e.date); if (isNaN(d)) return;
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!m[k]) m[k] = { month: k, debit: 0, credit: 0 };
      if (e.type === 'credit') m[k].credit += parseFloat(e.amount) || 0;
      else m[k].debit += parseFloat(e.amount) || 0;
    });
    return Object.values(m).sort((a, b) => a.month.localeCompare(b.month)).slice(-6);
  }, [allExpenses]);

  // ── Styles ──
  const pageStyle = { minHeight: '100vh', background: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
  const cardBg = '#fff';
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', background: '#fafafa' };
  const authInputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 16, background: '#fafafa', boxSizing: 'border-box', textAlign: 'center', letterSpacing: 1 };
  const btnGradient = 'linear-gradient(135deg, #2563eb, #3b82f6)';

  // ── Install banner component ──
  const installBanner = showInstall ? (
    <div style={{ width: '100%', maxWidth: 420, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
      }}>
        <span style={{ color: '#fff', fontSize: 13, fontWeight: 900, letterSpacing: 1 }}>MYC</span>
      </div>
      <div style={{ flex: 1, fontSize: 13, color: '#1e40af', lineHeight: 1.5 }}>
        <strong>Quick Access:</strong> Tap <strong>Share → Add to Home Screen</strong> to save this as a shortcut.
      </div>
      <button onClick={handleInstall} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        {deferredPrompt.current ? 'Install' : 'Got it'}
      </button>
    </div>
  ) : null;

  // ── Auth gate (not authenticated) ──
  if (!kioskAuth) {
    return (
      <div style={{ ...pageStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, gap: 16 }}>
        {installBanner}
        <div style={{ background: cardBg, borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,.08)', maxWidth: 420, width: '100%', overflow: 'hidden' }}>
          <div style={{ background: btnGradient, padding: '24px', color: '#fff', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: 3 }}>MYC</div>
            <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.8, textTransform: 'uppercase', marginTop: 2 }}>Kiosk Expenses</div>
          </div>
          <div style={{ padding: '24px 28px 32px' }}>
            {authStep === 'username' && (
              <form onSubmit={handleRequestOtp}>
                <div style={{ fontSize: 14, color: '#475569', marginBottom: 16, textAlign: 'center', lineHeight: 1.5 }}>
                  Enter your staff username to continue.
                </div>
                <input type="text" value={authUser} onChange={e => { setAuthUser(e.target.value); setAuthError(''); }}
                  placeholder="Username" style={authInputStyle} autoComplete="off" autoFocus />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#059669' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={{
                  width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
                  background: authLoading ? '#94a3b8' : btnGradient,
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: authLoading ? 'not-allowed' : 'pointer',
                }}>
                  {authLoading ? 'Checking...' : 'Continue'}
                </button>
              </form>
            )}
            {authStep === 'otp' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthOtp(''); setAuthError(''); }}
                    style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>
                  OTP sent to your registered WhatsApp number.
                </div>
                <input ref={otpRef} type="number" value={authOtp} onChange={e => { setAuthOtp(e.target.value.slice(0, 4)); setAuthError(''); }}
                  placeholder="4-digit OTP" style={authInputStyle} inputMode="numeric" />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#059669' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 10, textAlign: 'center' }}>
                  {authResend > 0 ? `Resend in ${authResend}s` : (
                    <button type="button" onClick={handleRequestOtp} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: 12, textDecoration: 'underline' }}>Resend OTP</button>
                  )}
                </div>
                <button type="submit" disabled={authLoading} style={{
                  width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
                  background: authLoading ? '#94a3b8' : btnGradient,
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: authLoading ? 'not-allowed' : 'pointer',
                }}>
                  {authLoading ? 'Verifying...' : 'Verify & Continue'}
                </button>
              </form>
            )}
            {authStep === 'password' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthPassword(''); setAuthError(''); }}
                    style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>
                  Enter your password to continue.
                </div>
                <input ref={pwRef} type="password" value={authPassword} onChange={e => { setAuthPassword(e.target.value); setAuthError(''); }}
                  placeholder="Password" style={{ ...authInputStyle, letterSpacing: 3 }} />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={{
                  width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
                  background: authLoading ? '#94a3b8' : btnGradient,
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: authLoading ? 'not-allowed' : 'pointer',
                }}>
                  {authLoading ? 'Verifying...' : 'Login & Continue'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Access denied (authenticated but missing remoteExpenseAccess or expenses.view) ──
  if (!hasAccess) {
    return (
      <div style={{ ...pageStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: cardBg, borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,.08)', maxWidth: 400, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>Access Denied</h2>
          <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, margin: '0 0 20px' }}>
            Your account does not have the required permissions to view expenses in kiosk mode.
            You need both <strong>Remote Expense Access</strong> and <strong>Expense View</strong> permission.
          </p>
          <button onClick={() => { localStorage.removeItem(KIOSK_AUTH_KEY); setKioskAuth(null); setAuthStep('username'); setAuthUser(''); setAuthOtp(''); setAuthPassword(''); }}
            style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Try Different Account
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // MAIN EXPENSES VIEW (authenticated + access verified, view-only)
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div style={pageStyle}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 12, left: 12, right: 12, zIndex: 99999,
          padding: '12px 16px', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,.15)',
          background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          color: toast.type === 'error' ? '#991b1b' : '#166534',
          fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>{toast.type === 'error' ? '!' : '✓'}</span>
          <span style={{ flex: 1 }}>{toast.message}</span>
          <button onClick={() => setToast(null)} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'inherit' }}>x</button>
        </div>
      )}

      {/* Install / Add to Home Screen banner */}
      {showInstall && (
        <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
          }}>
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 900, letterSpacing: 1 }}>MYC</span>
          </div>
          <div style={{ flex: 1, fontSize: 13, color: '#1e40af', lineHeight: 1.5 }}>
            <strong>Quick Access:</strong> Tap <strong>Share → Add to Home Screen</strong> to save this as a shortcut.
          </div>
          <button onClick={handleInstall} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {deferredPrompt.current ? 'Install' : 'Got it'}
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)', padding: '16px 16px 14px', color: '#fff', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Expenses</div>
            <div style={{ fontSize: 10, opacity: 0.7 }}>Staff: {kioskAuth.username} · View Only</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {canViewAnalytics && (
              <button onClick={() => setShowAnalytics(!showAnalytics)}
                style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: showAnalytics ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 14, cursor: 'pointer' }}>
                📊
              </button>
            )}
            {!kioskAuth.fromMainApp && (
              <button onClick={() => { localStorage.removeItem(KIOSK_AUTH_KEY); setKioskAuth(null); setAuthStep('username'); setAuthUser(''); setAuthOtp(''); setAuthPassword(''); }}
                style={{ padding: '6px 10px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 11, cursor: 'pointer' }}>
                Logout
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Balance chips — clickable to filter */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div onClick={() => setFilterType(filterType === 'credit' ? '' : 'credit')}
          style={{ flex: '0 0 auto', background: '#ecfdf5', border: filterType === 'credit' ? '2px solid #059669' : '1px solid #86efac', borderRadius: 10, padding: '8px 14px', minWidth: 90, cursor: 'pointer' }}>
          <div style={{ fontSize: 10, color: '#059669', fontWeight: 700, textTransform: 'uppercase' }}>↑ Credit{filterType === 'credit' ? ' ✓' : ''}</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#065f46' }}>{fmt(totalCredit)}</div>
        </div>
        <div onClick={() => setFilterType(filterType === 'debit' ? '' : 'debit')}
          style={{ flex: '0 0 auto', background: '#fef2f2', border: filterType === 'debit' ? '2px solid #dc2626' : '1px solid #fecaca', borderRadius: 10, padding: '8px 14px', minWidth: 90, cursor: 'pointer' }}>
          <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 700, textTransform: 'uppercase' }}>↓ Debit{filterType === 'debit' ? ' ✓' : ''}</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#991b1b' }}>{fmt(totalDebit)}</div>
        </div>
        <div onClick={() => { setFilterType(''); setFilterCategory(''); setFilterDesc(''); setSearchQuery(''); }}
          style={{ flex: '0 0 auto', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10, padding: '8px 14px', minWidth: 90, cursor: 'pointer' }}>
          <div style={{ fontSize: 10, color: '#2563eb', fontWeight: 700, textTransform: 'uppercase' }}>Balance (All)</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: (overallCredit - overallDebit) >= 0 ? '#1e40af' : '#dc2626' }}>{fmt(overallCredit - overallDebit)}</div>
        </div>
      </div>

      {/* Search + date filter toggle */}
      <div style={{ padding: '0 16px 8px', display: 'flex', gap: 8 }}>
        <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' }} />
        <button onClick={() => setShowDateFilter(!showDateFilter)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: showDateFilter ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: 13, color: '#475569' }}>
          📅
        </button>
      </div>

      {/* Category + Description filters */}
      <div style={{ padding: '0 16px 8px', display: 'flex', gap: 8 }}>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{ flex: '0 1 auto', padding: '7px 8px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12, background: '#fff', minWidth: 0, maxWidth: '45%' }}>
          <option value="">All Categories</option>
          {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="text" placeholder="Filter description…" value={filterDesc} onChange={e => setFilterDesc(e.target.value)}
          style={{ flex: 1, padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12, background: '#fff', minWidth: 0 }} />
      </div>
      {(filterType || filterCategory || filterDesc) && (
        <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#6b7280' }}>{filtered.length}/{expenses.length} results</span>
          <button onClick={() => { setFilterType(''); setFilterCategory(''); setFilterDesc(''); setSearchQuery(''); }}
            style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            Clear Filters
          </button>
        </div>
      )}

      {showDateFilter && (
        <div style={{ padding: '0 16px 12px', display: 'flex', gap: 8 }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ flex: 1, ...inputStyle, fontSize: 12 }} />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ flex: 1, ...inputStyle, fontSize: 12 }} />
        </div>
      )}

      {/* Analytics (collapsible) */}
      {showAnalytics && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ background: cardBg, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,.06)', padding: 16, marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>By Category</div>
            {byCat.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={byCat.slice(0, 6)} layout="vertical" margin={{ left: 60, right: 10, top: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={55} />
                  <Tooltip formatter={v => fmt(v)} />
                  <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 20 }}>No data</div>}
          </div>
          {byMonth.length > 1 && (
            <div style={{ background: cardBg, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,.06)', padding: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>Monthly Trend</div>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={byMonth} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip formatter={v => fmt(v)} />
                  <Line type="monotone" dataKey="debit" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="credit" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Expense cards (view-only) */}
      <div style={{ padding: '0 16px 100px' }}>
        {sorted.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 14 }}>No expenses found for this period.</div>
          </div>
        )}
        {sorted.map(e => {
          const isCredit = e.type === 'credit';
          const isExpanded = expandedId === e._id;
          return (
            <div key={e._id}
              onClick={() => setExpandedId(isExpanded ? null : e._id)}
              style={{
                background: cardBg, borderRadius: 12, marginBottom: 8,
                boxShadow: '0 1px 4px rgba(0,0,0,.06)',
                border: `1px solid ${isCredit ? '#d1fae5' : '#fee2e2'}`,
                overflow: 'hidden',
              }}>
              {/* Card header */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: isCredit ? '#ecfdf5' : '#fef2f2',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, fontWeight: 800,
                  color: isCredit ? '#059669' : '#dc2626',
                  flexShrink: 0,
                }}>
                  {isCredit ? '↑' : '↓'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.description || e.category || 'No description'}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                    {fmtDt(e.date)} · {e.paymentMode || 'Cash'}
                  </div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: isCredit ? '#059669' : '#dc2626', flexShrink: 0 }}>
                  {fmt(e.amount)}
                </div>
              </div>

              {/* Expanded detail (view-only, no edit/delete) */}
              {isExpanded && (
                <div style={{ padding: '0 14px 12px', borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', padding: '10px 0', fontSize: 12 }}>
                    <div><span style={{ color: '#94a3b8' }}>Category:</span> <span style={{ color: '#1e293b', fontWeight: 600 }}>{e.category || '-'}</span></div>
                    <div><span style={{ color: '#94a3b8' }}>Type:</span> <span style={{ color: '#1e293b', fontWeight: 600 }}>{e.type}</span></div>
                    <div><span style={{ color: '#94a3b8' }}>Sender:</span> <span style={{ color: '#1e293b', fontWeight: 600 }}>{e.sender || '-'}</span></div>
                    <div><span style={{ color: '#94a3b8' }}>Receiver:</span> <span style={{ color: '#1e293b', fontWeight: 600 }}>{e.receiver || '-'}</span></div>
                    <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#94a3b8' }}>Txn ID:</span> <span style={{ color: '#1e293b', fontWeight: 600 }}>{e.transactionId || '-'}</span></div>
                    {e.addedBy && <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#94a3b8' }}>Added by:</span> <span style={{ color: '#1e293b' }}>{e.addedBy}</span></div>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
